import gsap from "gsap";
import { Draggable } from "gsap/Draggable";

gsap.registerPlugin(Draggable);

export function initFactsSliders() {
    const factsSlider = document.querySelector(".pul-facts-slider");
    if (!factsSlider) return;

    const slider = factsSlider.querySelector('.pul-facts-slider__inner');
    const sliderItems = Array.from(slider.children);

    // Clone items for infinite loop effect
    for (let i = 0; i < 2; i++) {
        sliderItems.forEach(item => slider.appendChild(item.cloneNode(true)));
    }

    const lines = gsap.utils.toArray(slider);
    const timelines = [];

    lines.forEach((line, i) => {
        const links = line.querySelectorAll("div");
        const tl = horizontalLoop(links, {
            repeat: -1,
            draggable: true,
            paused: true // start paused, play when in viewport
        });

        links.forEach(link => {
            link.addEventListener("mouseenter", () =>
                gsap.to(tl, { timeScale: 0, overwrite: true })
            );
            link.addEventListener("mouseleave", () =>
                gsap.to(tl, { timeScale: i ? -1 : 1, overwrite: true })
            );
        });

        timelines.push(tl);
    });

    // IntersectionObserver to control playback
    const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            timelines.forEach(tl => {
                if (entry.isIntersecting) {
                    tl.play();
                } else {
                    tl.pause();
                }
            });
        });
    }, {
        threshold: 0.25 // play when at least 25% in viewport
    });

    observer.observe(factsSlider);

    // Horizontal loop logic
    function horizontalLoop(items, config = {}) {
        items = gsap.utils.toArray(items);
        const tl = gsap.timeline({
            repeat: config.repeat,
            paused: config.paused,
            defaults: { ease: "none" },
            onReverseComplete: () => tl.totalTime(tl.rawTime() + tl.duration() * 100)
        });

        const lengths = items.map(el => parseFloat(gsap.getProperty(el, "width", "px")));
        const xPercents = items.map((el, i) =>
            gsap.utils.snap(config.snap || 1)(
                parseFloat(gsap.getProperty(el, "x", "px")) / lengths[i] * 100 +
                gsap.getProperty(el, "xPercent")
            )
        );

        const startX = items[0].offsetLeft;
        gsap.set(items, { xPercent: (_, i) => xPercents[i], x: 0 });

        const getTotalWidth = () => {
            const last = items[items.length - 1];
            return last.offsetLeft +
                (xPercents[xPercents.length - 1] / 100 * lengths[lengths.length - 1]) -
                startX +
                last.offsetWidth * gsap.getProperty(last, "scaleX") +
                (parseFloat(config.paddingRight) || 0);
        };

        let totalWidth = getTotalWidth();
        const times = [];

        items.forEach((item, i) => {
            const curX = xPercents[i] / 100 * lengths[i];
            const distanceToStart = item.offsetLeft + curX - startX;
            const distanceToLoop = distanceToStart + lengths[i] * gsap.getProperty(item, "scaleX");

            tl.to(item, {
                xPercent: gsap.utils.snap(config.snap || 1)((curX - distanceToLoop) / lengths[i] * 100),
                duration: distanceToLoop / 60
            }, 0).fromTo(item, {
                xPercent: gsap.utils.snap(config.snap || 1)((curX - distanceToLoop + totalWidth) / lengths[i] * 100)
            }, {
                xPercent: xPercents[i],
                duration: (curX - distanceToLoop + totalWidth - curX) / 60,
                immediateRender: false
            }, distanceToLoop / 60);

            times[i] = distanceToStart / 60;
        });

        let curIndex = 0;
        const toIndex = (index, vars = {}) => {
            if (Math.abs(index - curIndex) > items.length / 2)
                index += index > curIndex ? -items.length : items.length;

            const newIndex = gsap.utils.wrap(0, items.length, index);
            let time = times[newIndex];
            if ((time > tl.time()) !== (index > curIndex)) {
                vars.modifiers = { time: gsap.utils.wrap(0, tl.duration()) };
                time += tl.duration() * (index > curIndex ? 1 : -1);
            }

            curIndex = newIndex;
            vars.overwrite = true;
            return tl.tweenTo(time, vars);
        };

        Object.assign(tl, {
            next: vars => toIndex(curIndex + 1, vars),
            previous: vars => toIndex(curIndex - 1, vars),
            current: () => curIndex,
            toIndex: (index, vars) => toIndex(index, vars),
            updateIndex: () => { curIndex = Math.round(tl.progress() * (items.length - 1)); },
            times
        });

        tl.progress(1, true).progress(0, true);

        // Draggable integration
        if (config.draggable && typeof Draggable === "function") {
            const proxy = document.createElement("div");
            const wrap = gsap.utils.wrap(0, 1);
            let ratio, startProgress, draggable, dragSnap, roundFactor;

            const align = () => tl.progress(wrap(startProgress + (draggable.startX - draggable.x) * ratio));
            const syncIndex = () => tl.updateIndex();

            draggable = Draggable.create(proxy, {
                trigger: items[0].parentNode,
                type: "x",
                onPress() {
                    startProgress = tl.progress();
                    tl.progress(0);
                    totalWidth = getTotalWidth();
                    ratio = 1 / totalWidth;
                    dragSnap = totalWidth / items.length;
                    roundFactor = 10 ** ((dragSnap + "").split(".")[1]?.length || 0);
                    tl.progress(startProgress);
                },
                onDrag: align,
                onThrowUpdate: align,
                inertia: true,
                snap: value => {
                    const n = Math.round(parseFloat(value) / dragSnap) * dragSnap * roundFactor;
                    return (n - n % 1) / roundFactor;
                },
                onRelease: syncIndex,
                onThrowComplete: () => {
                    gsap.set(proxy, { x: 0 });
                    syncIndex();
                }
            })[0];
        }

        return tl;
    }
}